import { rootStore } from '../grid/reduxStore/Store';

type SectionVisibleBounds = {
  sectionRect: DOMRect;
  visibleTop: number;
  visibleBottom: number;
};

export const getSectionVisibleBounds = (sectionId: string): SectionVisibleBounds | null => {
  const sectionCanvas = document.querySelector(`.canvas_${sectionId}`);

  if (!sectionCanvas) {
    return null;
  }

  const sectionRect = sectionCanvas.getBoundingClientRect();
  const visibleTop = Math.max(0, -sectionRect.top);
  const visibleBottom = Math.min(sectionRect.height, window.innerHeight - sectionRect.top);

  return { sectionRect, visibleTop, visibleBottom };
};

export const getVisibleHeight = (sectionId: string): number => {
  const bounds = getSectionVisibleBounds(sectionId);

  if (!bounds) {
    return 0;
  }

  return Math.max(0, bounds.visibleBottom - bounds.visibleTop);
};

export const getCenteredY = (sectionId: string, blockHeight: number): number => {
  const bounds = getSectionVisibleBounds(sectionId);

  if (!bounds) {
    return 0;
  }

  const { sectionRect, visibleTop, visibleBottom } = bounds;
  const visibleMiddle = visibleTop + (visibleBottom - visibleTop) / 2;
  const maxY = Math.max(0, sectionRect.height - blockHeight);

  return Math.min(Math.max(0, visibleMiddle - blockHeight / 2), maxY);
};

export const getGreaterZIndexAvailable = (sectionId: string): number => {
  return (
    rootStore.getState().gridBlockReducer.sections.find((section) => section.id === sectionId)?.blocksLayer.greaterZIndexAvailable ?? 0
  );
};

// Shared by every click-to-add-a-block hook (Table/Image/Embed, and any future one): pick the
// section a new block should land in when nothing was dragged to a specific spot. Preference
// order: the section the user has explicitly selected, then the currently active section, then
// the tallest currently-visible section (so the block lands somewhere on screen), then simply the
// first section as a last resort.
export const resolveTargetSectionId = (
  selectedSectionId: string | null | undefined,
  activeSection: string | undefined,
  sections: Array<{ id: string }>
): string | undefined => {
  return (
    selectedSectionId ??
    activeSection ??
    sections
      .map((section) => ({ sectionId: section.id, visibleHeight: getVisibleHeight(section.id) }))
      .sort((a, b) => b.visibleHeight - a.visibleHeight)[0]?.sectionId ??
    sections[0]?.id
  );
};
